import { Injectable } from '@angular/core';
import { Model } from 'survey-core';
import * as Survey from 'survey-core';
import {
  ApplicationsApiService,
  SnackbarService,
  ProductTypeCatalogApiService,
  OrientationSessionsApiService,
  CoursesOnDemandApiService,
  CoursesApiService,
  OfficesApiService,
  HttpOptions,
  ValidateDuesPaymentsApiService,
  NrdsMembersApiService,
  StatesApiService
} from '@tangilla/tg-ng-core';
import { catchError, EMPTY, map, of, switchMap, throwError } from 'rxjs';
import { License, RequestParameters } from '@tangilla/tg-ng-models';
import * as moment from 'moment';
import { SurveyService } from 'src/app/core/services/survey/survey.service';
import { ErrorsService } from 'src/app/core/services/utils/errors.service';
import { QuestionsService } from '../../../../core/services/survey/questions.service';
import { VariablesService } from '../../../../core/services/survey/variables.service';
import { OfficesUtilsService } from '../../../../core/services/utils/offices.utils.service';
import { MembershipApplicationValueChangesService } from './services/membership-application-value-changes-service';
import {
  ApplicationChargesUtilsService
} from '../../../../core/services/utils/charges/application-charges.utils.service';

@Injectable({
  providedIn: 'root'
})
export class MembershipApplicationService {
  formatter: Intl.NumberFormat | any;
  dr_workflow: boolean = false;

  constructor(
    private applicationsService: ApplicationsApiService,
    private productTypeCatalogService: ProductTypeCatalogApiService,
    private orientationSessionsApiService: OrientationSessionsApiService,
    private coursesOnDemandApiService: CoursesOnDemandApiService,
    private coursesApiService: CoursesApiService,
    private officesApiService: OfficesApiService,
    private applicationChargesUtilsService: ApplicationChargesUtilsService,
    private officesUtilsService: OfficesUtilsService,
    private surveyService: SurveyService,
    private validateDuesPaymentsApiService: ValidateDuesPaymentsApiService,
    private nrdsMembersApiService: NrdsMembersApiService,
    private errorsService: ErrorsService,
    private statesApiService: StatesApiService,
    private questionsService: QuestionsService,
    private variablesService: VariablesService,
    private membershipApplicationValueChangesService: MembershipApplicationValueChangesService,
  ) {
  }

  initializeForm(): any {
    this.formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });

    const office_search_state_q = this.surveyService.surveyModel.getQuestionByName('Office-Search-State');
    if (office_search_state_q) {
      office_search_state_q.value = this.surveyService.surveyModel.getVariable('v-state');
    }
    this.surveyService.surveyModel.setVariable('has_to_select_membership_type', true);
    if (this.surveyService.surveyModel.getVariable('member_status')) {
      this.surveyService.surveyModel.setValue('member_status', this.surveyService.surveyModel.getVariable('member_status'));
    }

    /*if(!clear_fwd_answers){
      console.warn('clear_fwd_answers not enabled');
    }*/

    this.setFormOnComplete();
    const default_orientation_membership_types = this.surveyService.surveyModel.getVariable('default_orientation_membership_types');
    if (!default_orientation_membership_types) {
      this.surveyService.surveyModel.setVariable('default_orientation_membership_types', ['NewM1', 'Transfer', 'ReinstateM1']);
    }

    if(!this.surveyService.surveyModel.getVariable('member_orientation_session_text')) {
      this.surveyService.surveyModel.setVariable('member_orientation_session_text', '<h3>New Member Orientation</h3><p class="infopanel">This course is designed for new members to learn more about services and value added to their membership with the {v-association}, {v-state_long} Association of REALTORS® and the National Association of REALTORS® (NAR).</p><p class="infopanel"><strong>As a primary member of the {v-association}, this course is required to be completed within {orientation_course_days} days of joining.</strong> Attending New Member Orientation fulfills the first year NAR REALTOR® Code of Ethics requirement. New REALTORS® will learn about member services, NAR Code of Ethics, {v-state_long} Association of REALTORS® benefits, advocacy, member profiles, safety, association governance and educational programming that will provide a foundation towards a successful business in real estate. </p>');
    }

    if (this.surveyService.surveyModel.getVariable('v-mls_terms_access')) {
      this.surveyService.surveyModel.getQuestionByName('mls_terms_access')['choices'] = this.surveyService.surveyModel.getVariable('v-mls_terms_access');
      if (this.surveyService.surveyModel.getVariable('ask_for_ethics') === 'No') {
        this.surveyService.surveyModel.getQuestionByName('terms').validators[0].setPropertyValue('minCount', 1);
      }
    }

    // Set Orientation Info Additional html if the orientation_info_additional variable has a value
    if (this.surveyService.surveyModel.getVariable('orientation_info_additional')) {
      this.surveyService.surveyModel.getQuestionByName('Orientation Info Additional')['html'] = this.surveyService.surveyModel.getVariable('orientation_info_additional');
    }

    // Here goes the navigation code for the membership application form
    this.initializeOnAfterRenderPage();
    this.initializeOnCurrentPageChanging();
    this.initializeOnValueChanged();

  }

  setFormOnComplete() {
    const that = this;

    this.surveyService.surveyModel.onComplete.add(function (result) {
      if (!that.surveyService.isPreview) {
        that.surveyService.surveyModel.setValue('Office-Search', '');
        that.surveyService.surveyModel.setValue('NRDSSearch', '');
        that.surveyService.surveyModel.setValue('AutoAddress', '');
      }
    });
  }

  initializeOnAfterRenderPage() {
    this.surveyService.surveyModel.onAfterRenderPage.add((sender, options) => {
      const currentPage = sender.currentPage;
      switch (currentPage.name) {
        case 'Orientation Session':
          this.loadOrientationEvents('Orientation');
          break;
        case 'Orientation Session Additional':
          this.loadAdditionalOrientationEvents('Orientation2', this.surveyService.surveyModel.getVariable('additional_orientation_is_on_demand'), this.surveyService.surveyModel.getVariable('additional_course_id'), this.surveyService.surveyModel.getVariable('additional_course_days'));
          break;
        case 'Ethics Orientation Session Additional':
          this.loadAdditionalOrientationEvents('EthicsOrientation', false, this.surveyService.surveyModel.getVariable('additional_ethics_course_id'), this.surveyService.surveyModel.getVariable('additional_ethics_course_days'));
          break;
        case 'TermsConditions':
          this.processTermsConditionsPage();
          break;
        case 'Confirmation':
          this.buildOrderConfirmation();
          break;
        case 'Transition Election Confirmation':
          break;
        case 'Membership Type Selection':
          const availableMembershipTypes = this.surveyService.surveyModel.getVariable('available_membership_types');
          if(!availableMembershipTypes || availableMembershipTypes.length === 0) {
            this.surveyService.surveyModel.getPageByName('Membership Type Selection').getQuestionByName('error_message').visible = true;
            this.surveyService.surveyModel.getPageByName('Membership Type Selection').getQuestionByName('error_message')['html'] = '<p><b>There are no available membership types for you to select. Please contact your association for more information.</b></p>';
            this.surveyService.surveyModel.getPageByName('Membership Type Selection').getQuestionByName('MembershipType').description = '';
          } else {
            this.surveyService.surveyModel.getPageByName('Membership Type Selection').getQuestionByName('error_message').visible = false;
            this.surveyService.surveyModel.getPageByName('Membership Type Selection').getQuestionByName('MembershipType').visible = true;
            this.surveyService.surveyModel.getPageByName('Membership Type Selection').getQuestionByName('MembershipType').description = 'Please select from the option below that best describes the type of membership you are applying for.'
          }
          break;
      }
    });
  }

  initializeOnCurrentPageChanging() {
    this.surveyService.surveyModel.onCurrentPageChanging.add((sender, options) => {
      if (options.oldCurrentPage !== null) {
        const thisPageName = options.oldCurrentPage.name;
        switch (thisPageName) {
          case 'License Details':
          case 'M1 Validation':
          case 'Your Office':
            // case 'DR Status':
            this.getAvailableMembershipTypes().subscribe();
            break;
        }

      }
    });
  }

  initializeOnValueChanged() {
    this.surveyService.surveyModel.onValueChanged.add((surveyModel, questionThatChanged) => {
      if (questionThatChanged.name === 'LicenseHeld') {
        if (surveyModel.getValue(questionThatChanged.name) === 'Yes') {
          this.surveyService.surveyModel.getPageByName('Your Details').visible = true;
          // this.getAvailableMembershipTypes().subscribe();
        } else {
          this.surveyService.surveyModel.getPageByName('Your Details').visible = false;
        }
      }
      if (questionThatChanged.name === 'isCorp') {
        this.officesUtilsService.setOfficeNameInSurvey(questionThatChanged.value);
      } else if (questionThatChanged.name == 'Orientation') {
        const event_id = surveyModel.getQuestionByName(questionThatChanged.name).value;
        const course_id = surveyModel.getVariable('orientation_is_on_demand') ? surveyModel.getVariable('on_demand_orientation_course_id') : surveyModel.getVariable('orientation_course_id');

        surveyModel.setValue('orientation_event_id', event_id);
        surveyModel.setValue('orientation_type', surveyModel.getVariable('orientation_is_on_demand') ? 'course_on_demand' : 'event');
        surveyModel.setValue('orientation', event_id);
        surveyModel.setValue('orientation_course_days', surveyModel.getVariable('orientation_course_days'));
        surveyModel.setValue('orientation_course_id', course_id);
        surveyModel.setValue('orientation_related_product', 'association');
      } else if (questionThatChanged.name == 'Orientation2') {
        surveyModel.setValue('orientation_event_id2', surveyModel.getQuestionByName(questionThatChanged.name).value);
        surveyModel.setValue('orientation2', surveyModel.getQuestionByName(questionThatChanged.name).displayValue);
        surveyModel.setValue('orientation2_type', surveyModel.getVariable('additional_orientation_is_on_demand') ? 'course_on_demand' : 'event');
        surveyModel.setValue('days_to_complete_orientation2', surveyModel.getVariable('additional_course_days'));
        surveyModel.setValue('orientation_course_id2', surveyModel.getVariable('additional_course_id'));
        surveyModel.setValue('orientation2_related_product', surveyModel.getVariable('additional_course_related_product'));
      } else if (questionThatChanged.name == 'EthicsOrientation') {
        surveyModel.setValue('ethics_orientation_event_id', surveyModel.getQuestionByName(questionThatChanged.name).value);
        surveyModel.setValue('ethics_orientation', surveyModel.getQuestionByName(questionThatChanged.name).displayValue);
        surveyModel.setValue('ethics_orientation_type', 'event');
        surveyModel.setValue('days_to_complete_ethics_orientation', surveyModel.getVariable('additional_ethics_course_days'));
        surveyModel.setValue('ethics_orientation_course_id', surveyModel.getVariable('additional_ethics_course_id'));
      } else if (questionThatChanged.name == 'MembershipType' && questionThatChanged.value) {
        this.surveyService.isLoading = true;
        if (this.surveyService.surveyModel.getValue('MembershipType') == 'New' || this.surveyService.surveyModel.getValue('MembershipType') == 'MLSOnlyLic') {
          this.surveyService.chargeable = true;
        } else if (questionThatChanged.value === 'Secondary') {
          // If the applicant is already a secondary - block the application
          // We do this by checking the member_record variable and seeing if in the member_supplementals array we already have a record with status A and association_nrds_id = current association
          const memberRecord = this.surveyService.surveyModel.getVariable('member_record');
          if (memberRecord && memberRecord.member_supplementals) {
            const isAlreadySecondary = memberRecord.member_supplementals.some((memberSupplemental: any) => {
              return memberSupplemental.association_nrds_id === this.surveyService.surveyModel.getVariable('v-association_nrds') && ['A', 'P'].includes(memberSupplemental.status);
            });
            if (isAlreadySecondary) {
              this.errorsService.exitForm('You are already a Secondary Member', `You are already a Secondary Member with ${this.surveyService.surveyModel.getVariable('v-association')}<br/>.` +
                `What a you trying to do today ? <br />` +
                `<li><strong>Applying for a new MLS Only Subscription?</strong> <a href="javascript:window.location.reload()">Start this form again</a> and select MLS ONLY</li>` +
                `<li><strong>Changing Brokerages?</strong> You will need to complete a <u>Change Brokerage Form</u></li>` +
                `<li><strong>Applying for a DR Transfer?</strong> You will need to complete a <u>DR Transfer Form</u>`);
            }
          }
        }

        this.reassessProductAndRole();
      } else if (questionThatChanged.name === 'office') {
        surveyModel.setVariable('show_DesignatedRealtor_question', true);
        const officeIdSelected = surveyModel.getValue('office');
        if (officeIdSelected) {
          this.surveyService.isLoading = true;
          this.officesApiService.getOffice(officeIdSelected).subscribe({
            next: (office: any) => {
              surveyModel.setVariable('selected_office', office);
              surveyModel.setVariable('office_name', office.business_name);
              surveyModel.setVariable('dr_name', office.field_description.designated_realtor);
              const applicantNrds = this.surveyService.surveyModel.getValue('NRDS');

              // If the applicant is already the DR of the office - set DesignatedRealtor to Already
              // if the office has a corporate license and the applicant is a broker, we have to validate whether the applicant is the sponsor of that license
              // if they are not, they can't take over the office as the DL
              // Moreover, if the office is a branch, they can't take over the office as the DL at all
              const drQuestion = this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor');
              if (applicantNrds && applicantNrds === office.field_description.designated_realtor_id || (this.surveyService.personId && this.surveyService.personId === office.tangilla_office.dl_person_id)) {
                drQuestion.value = 'Already';
                drQuestion.readOnly = true;
                surveyModel.setVariable('show_DesignatedRealtor_question', false);
              } else if (office.tangilla_office.main_office_id) {
                drQuestion.value = 'No';
                drQuestion.readOnly = true;
                surveyModel.setVariable('show_DesignatedRealtor_question', false);
              } else if (office.tangilla_office.corporate_license && this.surveyService.surveyModel.getValue('LicType') === 'Broker') {
                // Get corporate license info
                this.surveyService.isLoading = true;
                this.statesApiService.getStateLicense(this.surveyService.surveyModel.getVariable('v-state'), office.tangilla_office.corporate_license)
                  .subscribe({
                    next: (data: License) => {
                      if (data.broker_license_number !== this.surveyService.surveyModel.getValue('LicNumber')) {
                        drQuestion.value = 'No';
                        drQuestion.readOnly = true;
                        surveyModel.setVariable('show_DesignatedRealtor_question', false);
                      }
                    },
                    error: error => {
                      this.surveyService.isLoading = false;
                      this.errorsService.processError(error, 'hard');
                    }
                  });
              } else {
                if (['Secondary', 'MLSOnly'].includes(this.surveyService.surveyModel.getQuestionByName('MembershipType').value)) {
                  this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').value = 'No';
                  this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').readOnly = true;
                  surveyModel.setVariable('show_DesignatedRealtor_question', false);
                } else {
                  this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').clearValue();
                  this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').readOnly = false;
                }
              }

              if (this.surveyService.surveyModel.getVariable('office_class_transition_enabled')) {
                this.refreshTransitionRequiredFlag();
              }
              this.surveyService.isLoading = false;
            },
            error: (error) => {
              // Use the response from the search, find by office nrds id
              // Set office name and dr_name
              const officeSearchResult = this.surveyService.surveyModel.getVariable('office_search_result');
              if (officeSearchResult && officeSearchResult.length > 0) {
                const selectedOffice = officeSearchResult.find((office: any) => office.office_nrds_id === officeIdSelected);
                if (selectedOffice) {
                  surveyModel.setVariable('selected_office', selectedOffice);
                  surveyModel.setVariable('office_name', selectedOffice.business_name);
                  const dr_name = selectedOffice.dr_first_name + ' ' + selectedOffice.dr_last_name;
                  surveyModel.setVariable('dr_name', dr_name);

                  // Compare dr name with applicant's name with lowercasing
                  const applicant_name = surveyModel.getValue('FirstName') + ' ' + surveyModel.getValue('LastName');
                  if(dr_name.toLowerCase() === applicant_name.toLowerCase()) {
                    this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').value = 'Yes';
                    this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').readOnly = true;
                    surveyModel.setVariable('show_DesignatedRealtor_question', false);
                  } else {
                    this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').value = 'No';
                    this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').readOnly = true;
                    surveyModel.setVariable('show_DesignatedRealtor_question', false);
                  }

                } else {
                  this.surveyService.surveyModel.setVariable('selected_office', null);
                  this.surveyService.surveyModel.setVariable('office_name', null);
                  this.surveyService.surveyModel.setVariable('dr_name', null);
                  this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').clearValue();
                  this.surveyService.surveyModel.getQuestionByName('DesignatedRealtor').readOnly = false;
                }
              }
              this.surveyService.isLoading = false;
              this.errorsService.processError(error, 'soft');
            }
          });
        }
      } else if (questionThatChanged.name === 'Office-Search-Type') {
        this.membershipApplicationValueChangesService.officeSearchTypeValueChanges();
      } else if (questionThatChanged.name === 'NRDS') {
        if (surveyModel.getValue('NRDS')) {
          // Check for StateDuesStatus question value
          const parameters = (new RequestParameters(null)).setAdditionalFields({member_nrds_id: questionThatChanged.value});
          this.nrdsMembersApiService.get(parameters)
            .subscribe({
              next: (response: any) => {
                const duesStatus = response[0].state_association_id !== this.surveyService.surveyModel.getVariable('v-state_nrds') ? 'O' : 'S';
                this.surveyService.surveyModel.setValue('StateDuesStatus', duesStatus);
                this.surveyService.isLoading = false;
              },
              error: (error: any) => {
                this.surveyService.isLoading = false;
                this.errorsService.processError(error);
              }
            });

          this.validateDuesPayments();
        }
      } else if (questionThatChanged.name === 'DesignatedRealtor') {
        if (questionThatChanged.value) {
          this.getAvailableMembershipTypes().subscribe();
        }
      } else if (questionThatChanged.name === 'application_intention') {
        if (questionThatChanged.value) {
          this.getAvailableMembershipTypes().subscribe();
        }
      }
    });
  }

  reassessProductAndRole() {
    this.setRoleAndFetchProducts().subscribe({
      next: () => {
        if (this.surveyService.surveyModel.getValue('role')) {
          this.setPricingTable();
        }

        // Handle membership type selection to check if the office class matches it
        if (this.surveyService.surveyModel.getVariable('office_class_transition_enabled') && this.surveyService.surveyModel.getValue('MembershipType') && this.surveyService.surveyModel.getValue('MembershipType') !== 'NUA') {
          this.refreshTransitionRequiredFlag();
        }

        this.surveyService.isLoading = false;
      },
      error: (error) => {
        this.surveyService.isLoading = false;
        this.errorsService.processError(error);
      }
    });
  }

  private validateDuesPayments() {
    const memberNrdsId = this.surveyService.surveyModel.getValue('NRDS');
    const body = {
      member_nrds_id: memberNrdsId,
      billing_year: new Date().getFullYear()
    };

    this.validateDuesPaymentsApiService.create(body).subscribe({
      next: (response: any) => {

        if (response.state_dues === 'H') {
          // Stop form
          this.errorsService.exitForm('State Dues Not Paid', 'We are unable to proceed with your application. This might be because you have an open ethics incident. Please contact your local association to resolve this issue.');
        }

        // If state dues for the current period are paid set the StateDues question value
        if (this.surveyService.surveyModel.getQuestionByName('StateDuesPaid')) {
          if (response.state_dues === 'Y') {
            this.surveyService.surveyModel.getQuestionByName('StateDuesPaid').value = 'Yes';
          } else {
            this.surveyService.surveyModel.getQuestionByName('StateDuesPaid').value = 'No';
          }
        }

        // If national dues for the current period are paid set the NationalDues question value
        if (this.surveyService.surveyModel.getQuestionByName('NationalDuesPaid')) {
          if (response.nar_dues === 'Y') {
            this.surveyService.surveyModel.getQuestionByName('NationalDuesPaid').value = 'Yes';
          } else {
            this.surveyService.surveyModel.getQuestionByName('NationalDuesPaid').value = 'No';
          }
        }

        // If state dues are not paid but last year's state dues are paid, set flag to indicate proration should not be applied for state dues
        if (response.state_dues === 'N' && response.last_year_state_dues === 'Y') {
          this.surveyService.surveyModel.setVariable('state_dues_proration', false);
        } else {
          this.surveyService.surveyModel.setVariable('state_dues_proration', true);
        }

        // If national dues are not paid but last year's national dues are paid, set flag to indicate proration should not be applied for national dues
        if (response.nar_dues === 'N' && response.last_year_nar_dues === 'Y') {
          this.surveyService.surveyModel.setVariable('national_dues_proration', false);
        } else {
          this.surveyService.surveyModel.setVariable('national_dues_proration', true);
        }

        // If state dues for next year are paid set the StateDuesPaidNextYear question value
        if (this.surveyService.surveyModel.getQuestionByName('StateDuesPaidNextYear')) {
          if (response.next_year_state_dues === 'Y') {
            this.surveyService.surveyModel.getQuestionByName('StateDuesPaidNextYear').value = 'Yes';
          } else {
            this.surveyService.surveyModel.getQuestionByName('StateDuesPaidNextYear').value = 'No';
          }
        }

        // If national dues for next year are paid set the NationalDuesPaidNextYear question value
        if (this.surveyService.surveyModel.getQuestionByName('NationalDuesPaidNextYear')) {
          if (response.next_year_nar_dues === 'Y') {
            this.surveyService.surveyModel.getQuestionByName('NationalDuesPaidNextYear').value = 'Yes';
          } else {
            this.surveyService.surveyModel.getQuestionByName('NationalDuesPaidNextYear').value = 'No';
          }
        }

        this.surveyService.surveyModel.setVariable('state_dues_validated', true);
        this.surveyService.surveyModel.setVariable('national_dues_validated', true);
      },
      error: (error: any) => {
        this.surveyService.surveyModel.setVariable('state_dues_validated', false);
        this.surveyService.surveyModel.setVariable('national_dues_validated', false);
        this.errorsService.processError(error, 'soft');
      }
    });
  }

  private refreshTransitionRequiredFlag() {
    const currentRoleCode = this.surveyService.surveyModel.getValue('role') || null;
    const officeNrdsId = this.surveyService.surveyModel.getValue('office') || null;

    if (currentRoleCode) {
      const body = {
        role_code: currentRoleCode,
        office_nrds_id: officeNrdsId
      };
      this.surveyService.isLoading = true;
      this.applicationsService.getValidateTransitionRequired(body)
        .subscribe({
          next: (response: any) => {
            this.surveyService.surveyModel.setValue('transition_required', response.transition_required);
            // If transition required then set the tangilla_sub_class_from as the selected office's tangilla_office_class
            if (response.transition_required) {
              const office = this.surveyService.surveyModel.getVariable('selected_office');
              if (office && office.tangilla_office_class) {
                this.surveyService.surveyModel.setValue('tangilla_sub_class_from', office.tangilla_office_class);
              }

              if (response.can_partial_transition_to_realtor || response.can_partial_transition_to_mls) {
                this.surveyService.surveyModel.setValue('partial_transition', true);
              } else {
                this.surveyService.surveyModel.setValue('partial_transition', false);
              }
            } else {
              this.surveyService.surveyModel.setValue('tangilla_sub_class_from', null);
            }
            this.surveyService.isLoading = false;
          },
          error: (error: any) => {
            this.surveyService.isLoading = false;
            this.errorsService.processError(error, 'soft');
          }
        });

    } else {
      this.surveyService.surveyModel.setValue('tangilla_sub_class_from', null);
      this.surveyService.surveyModel.setValue('transition_required', false);
    }

  }

  setFormOnCompleting(surveyModel: Model) {
    const that = this;

    surveyModel.onCompleting.add(async function (survey, options) {
      if (survey.getVariable('waive_payment_on_application') !== true) {
        await that.checkZeroDollarSubmission(survey, options);
      }
    });
  }

  private loadAdditionalOrientationEvents(questionName: string, isOnDemand: boolean, courseId: string, maxDays: number) {
    const question = this.surveyService.surveyModel.getQuestionByName(questionName);

    if (!isOnDemand) {

      const options: HttpOptions = {
        params: {
          only_available_classes: 'true',
          order_by: 'event_date',
          order_by_direction: 'ASC',
          max_days: `${maxDays}`
        }
      };

      this.coursesApiService.getCourseClasses(courseId, options).subscribe({
        next: (data: any) => {
          question['choices'].splice(0, question['choices'].length); // clear existing options

          const days = maxDays;
          let location_type = '';
          let today = moment();
          for (const courseClass of data) {
            let edate = moment(courseClass['event_date']).subtract(days, 'days');
            if (edate.valueOf() < today.valueOf()) {
              location_type = courseClass['location_type'] === 'virtual' ? ' [Virtual]' : ' [In Person]';
              question['choices'].push(new Survey.ItemValue(courseClass['event_id'], courseClass['date_string'] + location_type + ' (' + courseClass['start_time_string'] + ' - ' + courseClass['end_time_string'] + ') @ ' + courseClass['location']));
            }
          }
          const item = new Survey.ItemValue(0, `I'll book into a session directly to be completed within ${maxDays} days`);
          question['choices'].push(item);
        },
        error: (error: any) => {
          question['choices'].splice(0, question['choices'].length); // clear existing options

          const item = new Survey.ItemValue(0, `I'll book into a session directly to be completed within ${maxDays} days`);
          question['choices'].push(item);
          this.errorsService.processError(error, 'soft');
        }
      });
    } else { // Load on demand course classes

      const params: RequestParameters = new RequestParameters(null);
      params.additionalFields = {course_id: courseId};
      this.coursesOnDemandApiService.getActiveCourses(params).subscribe({
        next: (on_demand_courses: any) => {
          question['choices'].splice(0, question['choices'].length); // clear existing options

          const days = maxDays;
          for (const on_demand of on_demand_courses) {
            question['choices'].push(new Survey.ItemValue(on_demand.course_on_demand_id, `${on_demand.course_name} - ${on_demand.days_of_access} days of access`));
          }

          const item = new Survey.ItemValue(0, `I'll book into a session directly to be completed within ${days} days`);
          question['choices'].push(item);
        },
        error: (error: any) => {
          question['choices'].splice(0, question['choices'].length); // clear existing options
          const item = new Survey.ItemValue(0, `I'll book into a session directly to be completed within ${maxDays} days`);
          question['choices'].push(item);
          this.errorsService.processError(error, 'soft');
        }
      });
    }
  }

  private loadOrientationEvents(questionName: string) {
    this.surveyService.isLoading = true;
    const question = this.surveyService.surveyModel.getQuestionByName(questionName);
    if (!this.surveyService.surveyModel.getVariable('orientation_is_on_demand')) {
      const params: RequestParameters = new RequestParameters(null);
      if (this.surveyService.surveyModel.getVariable('orientation_course_days') > 0) {
        params.additionalFields = {max_days: this.surveyService.surveyModel.getVariable('orientation_course_days')};
      }

      this.orientationSessionsApiService.getAll(params)
        .subscribe({
          next: (data: any) => {
            question['choices'].splice(0, question['choices'].length); // clear existing options
            for (const item of data) {
              const course_classes = item.course_classes;
              for (const course_class of course_classes) {
                let ev_type = ' [In Person] ';
                if (course_class.event_location_type.toLowerCase() === 'virtual') {
                  ev_type = ' [Virtual] ';
                }
                question['choices'].push(new Survey.ItemValue(course_class['event_id'], course_class['event_date'] + ev_type + ' (' + course_class['start_time'] + ' - ' + course_class['end_time'] + ') @ ' + course_class['event_location_name']));

              }
            }

            if (data[0] && data[0].course_id) {
              this.surveyService.surveyModel.setVariable('orientation_course_id', data[0].course_id);
            }

            const defaultItem = new Survey.ItemValue(0, `I'll book into a session directly to be completed within ${this.surveyService.surveyModel.getVariable('orientation_course_days')} days`);
            question['choices'].push(defaultItem);
            this.surveyService.isLoading = false;
          },
          error: (error: any) => {
            this.errorsService.processError(error, 'soft');
          }
        });
    } else if (this.surveyService.surveyModel.getVariable('orientation_is_on_demand') && this.surveyService.surveyModel.getVariable('on_demand_orientation_course_id')) { // Load on demand events
      const params: RequestParameters = new RequestParameters(null);
      params.additionalFields = {course_id: this.surveyService.surveyModel.getVariable('on_demand_orientation_course_id')};
      this.coursesOnDemandApiService.getActiveCourses(params).subscribe({
        next: (on_demand_courses: any) => {
          question['choices'].splice(0, question['choices'].length); // clear existing options

          let days = this.surveyService.surveyModel.getVariable('additional_course_days');
          for (const on_demand of on_demand_courses) {
            question['choices'].push(new Survey.ItemValue(on_demand.course_on_demand_id, `${on_demand.course_name} - ${on_demand.days_of_access} days of access`));
          }
          const item = new Survey.ItemValue(0, `I'll book into a session directly to be completed within ${days} days`);
          question['choices'].push(item);
        },
        error: (error: any) => {
          question['choices'].splice(0, question['choices'].length); // clear existing options
          const item = new Survey.ItemValue(0, `I'll book into a session directly to be completed within ${this.surveyService.surveyModel.getVariable('orientation_course_days')} days`);
          question['choices'].push(item);
        }
      });
    }

  }

  async checkZeroDollarSubmission(surveyModel: Model, options: any) {
    // if total charges is not greater than 0
    // and the application is for a role code that starts with R, D or S
    // This means this is a membership application and they should have been charged. We have to stop the form
    const totalCharge = surveyModel.getVariable('totalcharge');
    const roleCode = surveyModel.getValue('role');
    if ((!totalCharge || totalCharge <= 0) && (roleCode.startsWith('R') || roleCode.startsWith('D') || roleCode.startsWith('E'))) {

      options.allow = false; // stop the form from submitting
      options.allowComplete = false;

      this.surveyService.isLoading = true;

      surveyModel.setValue('sign', '');
      await this.errorsService.logToSlackSupportEscalations('There was an attempt to submit a Member Application and the charges for it were $0.', surveyModel.data, 'Member Application');
      this.errorsService.postHelpdeskTicketToAssociation(`Form Payment Issue - ${surveyModel.data.firstname} ${surveyModel.data.lastname}`, `${surveyModel.data.firstname} ${surveyModel.data.lastname} (${surveyModel.data.emailaddress}) has encountered a payment issue when attmepting to complete a Membership Application.\n This is queued for investigation by Tangilla.`);
      this.surveyService.isLoading = false;

      // general error message
      this.errorsService.exitForm('An error has ocurred', `We are having some trouble processing your Application. Our team has been notified of this issue. Please contact ${surveyModel.getVariable('v-association')} for further information.`);
    }
  }

  private urlParam(name: string) {
    var results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (results == null) {
      return null;
    }

    return decodeURI(results[1]) || 0;
  }

  private getAvailableMembershipTypesRequestBody() {
    return {
      license_data: {
        license_number: this.surveyService.surveyModel.getValue('LicNumber'), // || '9737890',
        license_name: this.surveyService.surveyModel.getValue('LicName'),
        license_state: this.surveyService.surveyModel.getValue('LicState'), // || 'TX',
        license_type: this.surveyService.surveyModel.getValue('LicType'), // || 'Broker',
        license_status: this.surveyService.surveyModel.getValue('LicStatus'),
        license_expiry: this.surveyService.surveyModel.getValue('LicExp'),
        broker_license_number: this.surveyService.surveyModel.getValue('BrkLic'),
        broker_license_type: this.surveyService.surveyModel.getValue('BrkLicType'),
      },
      member_nrds_id: this.surveyService.surveyModel.getValue('NRDS'),
      office_nrds_id: this.surveyService.surveyModel.getValue('office'),
      person_id: this.surveyService.surveyModel.getValue('person_id'),
      becomes_dr: this.surveyService.surveyModel.getValue('DesignatedRealtor') === 'Yes' || ['Yes', 'Transfer'].includes(this.surveyService.surveyModel.getValue('CorporateAffiliate')),
      application_intention: this.surveyService.surveyModel.getValue('application_intention'),
    };
  }


  getAvailableMembershipTypes() {
    this.surveyService.isLoading = true;
    const body = this.getAvailableMembershipTypesRequestBody();

    return this.applicationsService.getAvailableMembershipTypes(body)
      .pipe(
        map((data: any) => {

          if (data.person_id && data.person_id !== this.surveyService.surveyModel.getValue('person_id')) {
            this.surveyService.surveyModel.setValue('person_id', data.person_id);
            this.surveyService.personId = data.person_id;
          }
          if (data.member_nrds_id && data.member_nrds_id !== this.surveyService.surveyModel.getValue('NRDS')) {
            this.surveyService.surveyModel.setValue('NRDS', data.member_nrds_id);
            this.surveyService.surveyModel.setVariable('member_record', data.member_record);
            // Set chargeable flag
            // if member record's status is A, S, P or I - chargeable is true
            if (['A', 'S', 'P', 'I'].includes(data.member_record.status)) {
              this.surveyService.chargeable = true;
            }
          }

          if (typeof this.surveyService.surveyModel.getVariable('is_realtor_application') !== 'boolean') { // TODO: this is only needed for the first time the page loads
            this.surveyService.surveyModel.setVariable('is_realtor_application', data.is_realtor_application);
            this.surveyService.surveyModel.setVariable('is_non_realtor_application', data.is_non_realtor_application);
            this.surveyService.surveyModel.setVariable('can_be_realtor_and_non_realtor', data.can_be_realtor_and_non_realtor);
            this.surveyService.surveyModel.setVariable('can_be_mls_only_realtor_and_transfer_or_secondary', data.can_be_mls_only_realtor_and_transfer_or_secondary);
          }

          this.surveyService.surveyModel.setVariable('is_already_member', data.is_member);
          this.surveyService.surveyModel.setVariable('can_create_office', data.can_create_office);

          const membership_types = data.membership_types;
          this.surveyService.surveyModel.setVariable('available_membership_types', membership_types);
          const question = this.surveyService.surveyModel.getQuestionByName('MembershipType');
          question['choices'] = membership_types.map((mt: any) => new Survey.ItemValue(mt.code, mt.description));

          if (membership_types.length === 1) {
            // this.surveyService.surveyModel.setValue('MembershipType', ''); // This is to trigger the onValueChanged event
            const previousMembershipType = this.surveyService.surveyModel.getValue('MembershipType');
            this.surveyService.surveyModel.setValue('MembershipType', membership_types[0].code);
            this.surveyService.surveyModel.setVariable('has_to_select_membership_type', false);
            // For this case I need to reassess the role and products if the previous membership type was the same as the new one since the on value changed won't trigger
            if (previousMembershipType === membership_types[0].code) {
              this.reassessProductAndRole();
            }
          } else {
            this.surveyService.surveyModel.setValue('MembershipType', '');
            this.surveyService.surveyModel.setVariable('has_to_select_membership_type', true);
          }
          this.surveyService.isLoading = false;
        }),
        catchError((error: any) => {
          this.surveyService.isLoading = false;
          this.errorsService.processError(error, 'hard');
          return EMPTY;
        })
      );
  }

  setRoleAndFetchProducts() {
    if (this.surveyService.surveyModel.getValue('MembershipType')) {
      if (this.surveyService.surveyModel.getValue('Office-Search-Type') === 'NewOffice') {
        this.surveyService.surveyModel.setValue('DesignatedRealtor', 'Yes');
      }

      const body = {
        membership_type: this.surveyService.surveyModel.getValue('MembershipType'),
        CA: this.surveyService.surveyModel.getValue('CorporateAffiliate'),
        lic_type: this.surveyService.surveyModel.getValue('LicType'),
        occupation: this.surveyService.surveyModel.getValue('occupation'),
        reso_office_type: this.surveyService.surveyModel.getValue('reso_office_type'),
        DR: this.surveyService.surveyModel.getValue('DesignatedRealtor'),
        state_dues_status: this.surveyService.surveyModel.getValue('StateDuesStatus'),
        key_services: this.surveyService.surveyModel.getVariable('key_services'),
        apply: this.urlParam('apply'),
        NRDS: this.surveyService.surveyModel.getValue('NRDS'),
        member_record: this.surveyService.surveyModel.getVariable('member_record')
      };

      return this.applicationsService.getRole(body, 'member')
        .pipe(
          map((data: any) => {
            this.surveyService.surveyModel.setValue('role', data.role);
            this.surveyService.surveyModel.setValue('association_product_type', data.association_product_type);
            this.surveyService.surveyModel.setValue('reso_office_type', data.reso_office_type);
            this.dr_workflow = data.dr_workflow;
            this.surveyService.drWorkflow = data.dr_workflow;

            if (this.surveyService.surveyModel.getValue('person_id') && this.surveyService.surveyModel.getValue('office')) {
              // await validateIfPersonAlreadyApplied()
            }
          }),
          switchMap(() => this.getProductCatalogForRole())
        );
    } else {
      return EMPTY;
    }

  }

  private getCatalogQueryParams() {
    const qp: any = {
      defaults_only: true
    };

    if (
      (
        this.surveyService.surveyModel.getValue('Office-Search-Type') === 'Broker' ||
        ['Yes', 'Already'].includes(this.surveyService.surveyModel.getValue('DesignatedRealtor')) ||
        (
          this.surveyService.surveyModel.getValue('Office-Search-Type')?.toLowerCase() === 'office' &&
          this.surveyService.surveyModel.getValue('DesignatedRealtor') === 'No'
        )
      ) &&
      this.surveyService.surveyModel.getValue('office')
    ) {
      qp.office_nrds_id = this.surveyService.surveyModel.getValue('office');
    }

    if (this.surveyService.surveyModel.getValue('NRDS')) {
      qp.member_nrds_id = this.surveyService.surveyModel.getValue('NRDS');
    }

    if (this.surveyService.surveyModel.getValue('StateDuesPaid') === 'Yes') {
      qp.exclude_state_dues = true;
    }

    if (this.surveyService.surveyModel.getValue('NationalDuesPaid') === 'Yes') {
      qp.exclude_national_dues = true;
    }

    if (this.surveyService.surveyModel.getValue('NationalDuesPaidNextYear') === 'Yes') {
      qp.exclude_national_next_year = true;
    }

    if (this.surveyService.surveyModel.getValue('StateDuesPaidNextYear') === 'Yes') {
      qp.exclude_state_next_year = true;
    }

    if (this.surveyService.surveyModel.getValue('person_id')) {
      qp.person_id = this.surveyService.surveyModel.getValue('person_id');
    }

    if (typeof this.surveyService.surveyModel.getVariable('state_dues_proration') === 'boolean') {
      qp.state_dues_proration = this.surveyService.surveyModel.getVariable('state_dues_proration');
    }

    if (typeof this.surveyService.surveyModel.getVariable('national_dues_proration') === 'boolean') {
      qp.national_dues_proration = this.surveyService.surveyModel.getVariable('national_dues_proration');
    }

    // const is_creating_new_office = req.query.is_creating_new_office
    if (['NewOffice'].includes(this.surveyService.surveyModel.getValue('Office-Search-Type'))) {
      qp.is_creating_new_office = true;
    }

    if (this.surveyService.surveyModel.getValue('LicType')) qp.license_type = this.surveyService.surveyModel.getValue('LicType');
    if (this.surveyService.surveyModel.getValue('MembershipType')) qp.membership_type = this.surveyService.surveyModel.getValue('MembershipType');
    if (this.surveyService.surveyModel.getValue('DesignatedRealtor')) qp.dr_q_value = this.surveyService.surveyModel.getValue('DesignatedRealtor');


    return qp;
  }

  getProductCatalogForRole() {
    const qp = this.getCatalogQueryParams();

    return this.productTypeCatalogService.getProductTypeCatalogForRoleCode(this.surveyService.surveyModel.getValue('role'), qp)
      .pipe(
        map((data: any) => {
          this.surveyService.surveyModel.setVariable('product_catalog', data);
          // Loop through catalog and make the necessary checks
          for (const product_name in data.products) {
            const product_types = data.products[product_name].product_types;
            const product_id = data.products[product_name].product_id;
            this.surveyService.surveyModel.setVariable(`${product_name}ProductID`, product_id);
            if (product_name === 'MLS' && product_types.length === 0 && (this.surveyService.surveyModel.getValue('MembershipType') === 'MLSOnly' || this.surveyService.surveyModel.getValue('MembershipType') === 'NUA')) {
              const title = `We are unable to retrieve ${product_name} products for your application.`;
              const message = `This can happen when the Designated Licensee's subscription in the office you selected has been suspended. Please contact your sponsor.`;
              this.errorsService.exitForm(title, message);
              this.surveyService.surveyModel.setVariable('dr_mls_status_ok', false);
            }

            const productQuestion = this.surveyService.surveyModel.getQuestionByName(product_name + 'Products');
            if (productQuestion) {
              if (productQuestion['choices'].length > 0) {
                productQuestion['choices'].splice(0, productQuestion['choices'].length); // clear existing options from the product question
              }

              for (let x = 0; x < product_types.length; x++) {

                const item = new Survey.ItemValue(product_types[x].id, product_types[x].description);
                if (!productQuestion['choices']) {
                  productQuestion['choices'] = [];
                }
                productQuestion['choices'].push(item);

                if (product_types[x].is_coop) {
                  this.surveyService.surveyModel.setValue('coop_product', product_types[x].id);
                }
                // Add required products
                if (product_types[x].required_product_ids && product_types[x].required_product_ids.length > 0) {
                  for (const required_product_id of product_types[x].required_product_ids) {
                    let required_product;
                    let required_product_key;
                    for (const key of Object.keys(data.products)) {
                      if (data.products[key].product_id === required_product_id) {
                        required_product = data.products[key];
                        required_product_key = key;
                        break;
                      }
                    }

                    // TODO: this is a workaround for frontend of choice for TMLS because the rule is that they should all get the FENS product type on application
                    // regardless of multiple selections being allowed
                    // Their product config currently has multiple products
                    if (required_product_key === 'FOC1' && data.products[required_product_key].product_types.length > 1) {
                      data.products[required_product_key].product_types = data.products[required_product_key].product_types.filter((pt: any) => pt.id === 'FENS');
                    }

                    // Get product types of required product, if it's just one, add product type to pricing table and autoselect it
                    if (required_product_key && data.products[required_product_key].product_types.length === 1) {
                      // const required_product_product_type = data.products[required_product_key].product_types[0]
                      // setProductTypePricingTable(required_product_product_type, PricingTable, qp)
                      if (this.surveyService.surveyModel.getQuestionByName(`${product_name}Requires${required_product_key}`)) {
                        this.surveyService.surveyModel.getQuestionByName(`${product_name}Requires${required_product_key}`).value = 'Yes';
                      }
                      if (this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`)) {
                        this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`).value = 'Yes';
                        this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Products`).value = data.products[required_product_key].product_types[0].id;
                      }

                      const product_id_question = this.surveyService.surveyModel.getQuestionByName(`${required_product_key}ProductId`);

                      if (product_id_question) {
                        product_id_question.value = required_product_id;
                      }

                    } else {
                      // Multiple selection here, the user cannot choose to not select anything in the product so we have to remove the
                      // choice to select 'No'
                      if (this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`)) {
                        this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`)['choices'] = [
                          {
                            'value': 'Yes',
                            'text': 'Yes'
                          },
                        ];
                        this.surveyService.surveyModel.getQuestionByName(`${required_product_key}Access`).value = 'Yes';
                      }
                    }
                  }
                }
              }

              this.surveyService.surveyModel.setVariable(`CanSelect${product_name}`, true);
            }

            // If there is no association product available, selecting No for MLSAccess question is not allowed, we need to remove the option from the question
            if (product_name === 'MLS') {
              const associationProductType = this.surveyService.surveyModel.getValue('association_product_type');
              const mlsAccessQuestion = this.surveyService.surveyModel.getQuestionByName('MLSAccess');
              if (!associationProductType && mlsAccessQuestion) {
                mlsAccessQuestion['choices'] = [
                  {
                    'value': 'Yes',
                    'text': 'Yes'
                  },
                ];
                mlsAccessQuestion.value = 'Yes';
                mlsAccessQuestion.visible = false;
              }
            }
          }

        }),
        catchError((error: any) => {
          this.errorsService.processError(error);
          return throwError(error);
        })
      );
  }


  private setPricingTable() {
    const data = this.surveyService.surveyModel.getVariable('product_catalog');
    let row = 0;
    for (const product_name in data.products) {

      const product = data.products[product_name];

      let title = 'Available ' + product.label + ' products';

      const that = this;

      let processedData: {
        title: string;
        items: Array<{
          id: number;
          firstValue: string;
          secondValue: string;
          details: any[];
          detailsCount: number;
          showDetails: boolean;
        }>;
        showColumnsTitles: boolean;
        withExpandButton: boolean;
        footer: string;
      } = {
        title: title,
        items: [],
        showColumnsTitles: false,
        withExpandButton: true,
        footer: '^ includes tax'
      };

      let itemId = 0;
      let detailId = 0;
      for (let x = 0; x < product.product_types.length; x++) {

        let item: {
          id: number;
          firstValue: string;
          secondValue: string;
          details: any[];
          detailsCount: number;
          showDetails: boolean;
        } = {
          id: itemId++,
          firstValue: '',
          secondValue: '',
          details: [],
          detailsCount: 0,
          showDetails: false
        };

        let product_type = product.product_types[x];

        var fee = 0;
        var totalFee = 0;
        var label = '';
        var longDescription = '';
        let detailsCount = 0;
        for (let b = 0; b < product_type.bill_periods.length; b++) {
          detailsCount++;
          let detail = {
            id: detailId,
            firstValue: '',
            secondValue: ''
          };
          if (product_type.bill_periods[b].proration_applied) {
            label = 'Prorated charges for the period ' + product_type.bill_periods[b].start_date + ' to ' + product_type.bill_periods[b].end_date;
          } else {
            label = 'Charges for the billing period ' + product_type.bill_periods[b].start_date + ' to ' + product_type.bill_periods[b].end_date;
          }
          detail = {
            id: detailId++,
            firstValue: label,
            secondValue: ''
          };
          item.details.push(detail);

          if (product_type.bill_periods[b].fees.length == 0) {
            detail = {
              id: detailId++,
              firstValue: 'There are no charges for this period.',
              secondValue: ''
            };
            item.details.push(detail);
          }

          for (let y = 0; y < product_type.bill_periods[b].fees.length; y++) {
            detailsCount++;
            fee = parseFloat(product_type.bill_periods[b].fees[y].price) + parseFloat(product_type.bill_periods[b].fees[y].tax);
            if (parseFloat(product_type.bill_periods[b].fees[y].tax) != 0) {
              label = product_type.bill_periods[b].fees[y].description + ' ^ ';
            } else {
              label = product_type.bill_periods[b].fees[y].description;
            }
            detail = {
              id: detailId++,
              firstValue: label,
              secondValue: `${that.formatter.format(fee)}`
            };
            item.details.push(detail);

            totalFee += fee;
          }
        }

        if (product_type.long_description != '') { // include a product type description if one exists
          longDescription = '<br/>' + product_type.long_description;
        }

        item.firstValue = `<strong>${product_type.description}</strong> ${longDescription}`;
        item.secondValue = `<strong>${that.formatter.format(totalFee)}`;
        item.detailsCount = detailsCount;
        detailId = 0;
        processedData.items.push(item);
      }

      this.surveyService.surveyModel.setVariable(product_name + 'DetailsList', processedData);

      this.surveyService.surveyModel.setVariable('CanSelect' + product_name, true);
    }
  }

  processTermsConditionsPage() {
    const termsQuestion = this.questionsService.terms;
    if (termsQuestion) {
      termsQuestion['choices'] = this.variablesService.terms || [];
      if(this.surveyService.surveyModel.getQuestionByName('terms').validators.length > 0){
       (this.surveyService.surveyModel.getQuestionByName('terms').validators[0] as any).minCount = this.variablesService.terms.length;
      }
    }
  }

  buildOrderConfirmation() {
    // Call charges endpoint
    const requestBody = this.getCatalogQueryParams();
    requestBody.recn_chargeable = this.surveyService.chargeable;
    requestBody.prorrate = true;
    requestBody.role_code = this.surveyService.surveyModel.getValue('role');
    requestBody.products = [];

    // Check Association product selection
    const associationProductType = this.surveyService.surveyModel.getValue('association_product_type');
    const associationProductID = this.surveyService.surveyModel.getVariable('AssociationProductID');
    if (associationProductType) {
      requestBody.products.push({
        product_id: associationProductID,
        product_type: associationProductType
      });
    }

    // Check MLS product selection
    const mlsProductType = this.surveyService.surveyModel.getValue('MLSProducts');
    const mlsProductID = this.surveyService.surveyModel.getVariable('MLSProductID');
    if (mlsProductType) {
      requestBody.products.push({
        product_id: mlsProductID,
        product_type: mlsProductType
      });
    }

    // Check Lockbox product selection
    const lockboxProduct = this.surveyService.surveyModel.getValue('LockboxProducts');
    const lockboxProductID = this.surveyService.surveyModel.getVariable('LockboxProductID');
    if (lockboxProduct) {
      requestBody.products.push({
        product_id: lockboxProductID,
        product_type: lockboxProduct
      });
    }

    // We need to add any additional NewOffice fee and transition_election fee to the charges
    requestBody.additional_invoice_template_ids = [];
    const invoice_templates = this.surveyService.surveyModel.getVariable('invoice_templates');
    if (this.surveyService.surveyModel.getValue('Office-Search-Type') && invoice_templates[this.surveyService.surveyModel.getValue('Office-Search-Type')]) {
      requestBody.additional_invoice_template_ids.push(invoice_templates[this.surveyService.surveyModel.getValue('Office-Search-Type')]);
    }
    if (this.surveyService.surveyModel.getValue('transition_required') && invoice_templates['transition_election']) { // TODO: retest this!!!
      requestBody.additional_invoice_template_ids.push(invoice_templates['transition_election']);
    }

    this.surveyService.isLoading = true;
    this.applicationsService.getApplicationCharges(requestBody)
      .subscribe({
        next: (data: any) => {
          this.applicationChargesUtilsService.processApplicationCharges(data);
          this.surveyService.isLoading = false;
        },
        error: (error: any) => {
          this.errorsService.processError(error);
        }
      });
  }
}
